import Button from "@/components/Button"
import DevErrorToast from "@/components/DevErrorToast"
import InputErrorMessage from "@/components/InputErrorMessage.tsx"
import InputLabel from "@/components/InputLabel"
import Spinner from "@/components/Spinner"
import TimePicker from "@/components/TimePicker"
import Toast from "@/components/Toast"
import { useUpdateRemindersMutation } from "@/data/queries.ts"
import {
  DAY_OF_WEEK_VALUES,
  DayOfWeek,
  ReminderDayTime,
  ReminderType,
} from "@/model/Reminders"
import DayOfWeekToggle from "@/pages/patient-details/reminders/DayOfWeekToggle"
import { reminderSchema } from "@/schemas/zod-schemas"
import { formatTime } from "@/utils/date-utils"
import { zodResolver } from "@hookform/resolvers/zod"
import { DateTimeFormatter, LocalTime } from "@js-joda/core"
import { Locale } from "@js-joda/locale"
import { uniqWith } from "lodash"
import { Controller, useForm } from "react-hook-form"
import { toast } from "sonner"

type ReminderFormProps = {
  bloomlifeId: string
  onCancel: () => void
  patientReminders: ReminderDayTime[]
}

type ReminderFormData = {
  time: string
  days: DayOfWeek[]
  type: ReminderType
}

const ReminderForm = ({
  bloomlifeId,
  onCancel,
  patientReminders,
}: ReminderFormProps) => {
  const { isPending: isUpdatingReminders, mutateAsync: updateReminders } =
    useUpdateRemindersMutation(bloomlifeId)
  const { handleSubmit, control, watch, formState, setValue } =
    useForm<ReminderFormData>({
      mode: "onChange",
      defaultValues: {
        time: formatTime(LocalTime.parse("12:00"), "hh:mm a"),
        days: [],
        type: "measurement",
      },
      resolver: zodResolver(reminderSchema),
    })
  const { errors } = formState
  const { time } = watch()

  const onSubmit = async (formData: ReminderFormData) => {
    const time12HourFormatter = DateTimeFormatter.ofPattern(
      "hh:mm a"
    ).withLocale(Locale.US)
    const newReminders = formData.days.map((dayOfWeek) => ({
      dayOfWeek,
      time: LocalTime.parse(formData.time, time12HourFormatter),
      type: "measurement" as ReminderType,
    }))
    const updatedReminders = uniqWith(
      [...patientReminders, ...newReminders],
      (a, b) => a.dayOfWeek === b.dayOfWeek && a.time.equals(b.time)
    )
    try {
      await updateReminders({
        reminderType: "measurement",
        reminders: updatedReminders,
      })
      onCancel()
      toast.custom((toastId) => (
        <Toast toastId={toastId} description="Reminders added successfully." />
      ))
    } catch (error) {
      toast.custom((toastId) => (
        <DevErrorToast
          toastId={toastId}
          userMessage="Something went wrong while adding the reminder. Try again, or contact the dev team."
          error={error}
        />
      ))
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="flex gap-6">
        <div className="flex gap-3">
          <InputLabel className="mt-2">At</InputLabel>
          <div className="max-w-[200px]">
            <TimePicker
              value={time}
              onChange={(time) => setValue("time", time)}
              errorMessage={errors?.time?.message}
            />
          </div>
        </div>
        <div className="mt-2 flex gap-3">
          <InputLabel className="mr-2">on</InputLabel>
          <div>
            <div className="flex gap-2">
              {DAY_OF_WEEK_VALUES.map((day) => (
                <div key={day}>
                  <Controller
                    name="days"
                    control={control}
                    render={({ field }) => (
                      <DayOfWeekToggle
                        value={day}
                        title={day[0].toUpperCase()}
                        checked={
                          field.value?.some(
                            (selectedDay) => selectedDay === day
                          ) || false
                        }
                        onChange={(e) => {
                          const selectedDays = field.value || []
                          if (e.target.checked) {
                            // Add day object to array if checked
                            field.onChange([...selectedDays, day])
                          } else {
                            // Remove the day object from array if unchecked
                            field.onChange(
                              selectedDays.filter(
                                (selectedDay) => selectedDay !== day
                              )
                            )
                          }
                        }}
                      />
                    )}
                  />
                </div>
              ))}
            </div>
            <InputErrorMessage
              message={errors?.days?.message}
              className="mt-1"
            />
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-3 pt-6">
        <Button
          type="button"
          variant="neutral"
          className="px-6"
          disabled={isUpdatingReminders}
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="primary"
          className="flex items-center gap-3 px-6"
          disabled={isUpdatingReminders}
        >
          {isUpdatingReminders && <Spinner className="size-4" />}
          Add Reminder
        </Button>
      </div>
    </form>
  )
}

export default ReminderForm
